<template>
  <b-navbar class="layout-navbar" toggleable="lg" variant="transparent" type="light">
    <!-- Merge in one div so the logo is not pushed to the right when menu appears. -->
    <div class="layout-navbar__menu-and-logo" style="">
      <b-navbar-toggle v-if="user" target="nav-collapse" />
      <b-navbar-brand :href="logoUrl" :to="logoRoute">
        <img src="/logo.svg" alt="Locomotion" />
      </b-navbar-brand>
    </div>

    <b-collapse class="layout-navbar__collapse" is-nav>
      <b-navbar-nav class="mr-auto">
        <b-navbar-brand v-if="title" class="layout-navbar__separator d-none d-sm-block" />
        <b-navbar-brand
          v-if="title"
          class="navbar-brand--title d-none d-sm-block"
          :style="{ 'view-transition-name': 'page-title' }"
        >
          {{ title }}
        </b-navbar-brand>
      </b-navbar-nav>
    </b-collapse>

    <b-collapse id="nav-collapse" class="layout-navbar__collapse" is-nav>
      <div class="layout-navbar__collapse__illustration d-md-none" />

      <b-navbar-nav v-if="user" class="ml-auto">
        <b-nav-item v-if="!isGlobalAdmin && userIsRegistered" to="/app">
          <span class="nav-link__icon d-lg-none">
            <svg-dashboard />
          </span>
          <span class="nav-link__text">{{ $t("titles.dashboard") | capitalize }}</span>
        </b-nav-item>

        <b-nav-item v-if="!isGlobalAdmin && canBorrowVehicle" to="/search/map">
          <span class="nav-link__icon d-lg-none">
            <svg-location />
          </span>
          <span class="nav-link__text">{{ $t("components.layout.navbar.borrow_vehicle") }}</span>
        </b-nav-item>

        <b-nav-item
          v-if="!isGlobalAdmin && userIsRegistered"
          to="/profile/locomotion"
          class="d-block d-lg-none"
        >
          <span class="nav-link__icon">
            <svg-profile />
          </span>
          <span class="nav-link__text">{{ $t("components.layout.navbar.profile") }}</span>
          <b-badge v-if="hasUnvalidatedCommunities" class="ml-2" href="#" pill variant="warning"
            >!</b-badge
          >
        </b-nav-item>

        <b-nav-item class="d-block d-lg-none" @click="logout">
          <span class="nav-link__icon">
            <svg-logout />
          </span>
          <span class="nav-link__text">{{ $t("components.layout.navbar.logout") }}</span>
        </b-nav-item>

        <b-nav-item-dropdown
          v-if="isAdmin"
          ref="admindropdown"
          class="layout-navbar__admin"
          :text="$t('components.layout.navbar.admin')"
          right
        >
          <admin-sidebar
            section-class="dropdown-item"
            :is-global-admin="isGlobalAdmin"
            item-component="b-dropdown-item"
            section-component="li"
            @click="$refs.admindropdown.hide()"
          >
          </admin-sidebar>
        </b-nav-item-dropdown>

        <navbar-locale-switcher />

        <b-nav-item-dropdown class="layout-navbar__dropdown d-none d-lg-block" text="" right>
          <template #button-content>
            <div class="layout-navbar__dropdown__avatar">
              <user-avatar :user="user" />

              <b-badge v-if="hasUnvalidatedCommunities" href="#" pill variant="warning">!</b-badge>
            </div>
          </template>
          <b-dropdown-text
            v-if="!isGlobalAdmin && userIsRegistered"
            text-class="d-flex p-2 align-items-center"
          >
            <user-avatar :user="user" />
            <span class="dropdown-container">
              <span class="username"> {{ user.name }}</span>
            </span>
          </b-dropdown-text>
          <b-dropdown-divider v-if="!isGlobalAdmin && userIsRegistered" />
          <b-dropdown-item v-if="!isGlobalAdmin && userIsRegistered" to="/profile/loans">
            <span class="nav-link__icon">
              <b-icon icon="arrow-repeat" />
            </span>
            <span class="nav-link__text">{{ $t("components.layout.navbar.my_loans") }}</span>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="!isGlobalAdmin && userIsRegistered && canAddLoanables"
            to="/profile/loanables"
          >
            <span class="nav-link__icon">
              <b-icon icon="bicycle" />
            </span>
            <span class="nav-link__text">{{ $t("components.layout.navbar.my_vehicles") }}</span>
          </b-dropdown-item>
          <b-dropdown-item v-if="!isGlobalAdmin && userIsRegistered" to="/profile/locomotion">
            <div class="d-flex w-100 align-items-center">
              <span class="nav-link__icon">
                <b-icon icon="person" />
              </span>
              <span class="nav-link__text flex-grow-1">{{
                $t("components.layout.navbar.my_profile")
              }}</span>

              <b-badge v-if="hasUnvalidatedCommunities" class="ml-2" href="#" pill variant="warning"
                >1</b-badge
              >
            </div>
          </b-dropdown-item>
          <b-dropdown-divider v-if="!userIsRegistered || !isGlobalAdmin" />
          <b-dropdown-item @click="logout">
            <span class="nav-link__icon">
              <b-icon icon="door-open-fill" />
            </span>
            <span class="nav-link__text">{{ $t("components.layout.navbar.logout") }}</span>
          </b-dropdown-item>
        </b-nav-item-dropdown>
      </b-navbar-nav>

      <b-navbar-nav v-else class="ml-auto">
        <navbar-locale-switcher />
      </b-navbar-nav>

      <div class="layout-navbar__collapse__buffer" />
    </b-collapse>
  </b-navbar>
</template>

<script>
import Dashboard from "@/assets/svg/dashboard.svg";
import Location from "@/assets/svg/location.svg";
import Logout from "@/assets/svg/logout.svg";
import Profile from "@/assets/svg/profile.svg";

import AdminSidebar from "@/components/Admin/Sidebar.vue";
import NavbarLocaleSwitcher from "@/components/Layout/NavbarLocaleSwitcher.vue";
import UserAvatar from "@/components/User/Avatar.vue";

import { canAddLoanables, canBorrowVehicle, userIsRegistered } from "@/helpers/permissions/users";
import { isAdminOfSomeCommunity } from "@/helpers/permissions/communities";

export default {
  name: "MainNavbar",
  components: {
    AdminSidebar,
    NavbarLocaleSwitcher,
    UserAvatar,
    "svg-dashboard": Dashboard,
    "svg-location": Location,
    "svg-logout": Logout,
    "svg-profile": Profile,
  },
  props: {
    title: {
      type: String,
      required: false,
      default: "",
    },
  },
  computed: {
    canAddLoanables() {
      return canAddLoanables(this.user);
    },
    canBorrowVehicle() {
      return this.user && canBorrowVehicle(this.user);
    },
    hasUnvalidatedCommunities() {
      return this.user.user_communities.some(
        (uc) => uc.status !== "approved" && uc.proof_state !== "valid"
      );
    },
    isGlobalAdmin() {
      return this.user && this.user.role === "admin";
    },
    isAdmin() {
      return this.isGlobalAdmin || isAdminOfSomeCommunity(this.user);
    },
    logoUrl() {
      if (process.env.VUE_APP_INFO_WEBSITE_URL) {
        return process.env.VUE_APP_INFO_WEBSITE_URL;
      }
      return "";
    },
    logoRoute() {
      if (!process.env.VUE_APP_INFO_WEBSITE_URL) {
        return "/app";
      }
      return "";
    },
    user() {
      return this.$store.state.user;
    },
    userIsRegistered() {
      return this.user && userIsRegistered(this.user);
    },
  },
  methods: {
    logout() {
      this.$store.dispatch("logout");

      this.$store.commit("addNotification", {
        content: this.$t("components.layout.navbar.notifications.logout.content"),
        title: this.$t("components.layout.navbar.notifications.logout.title"),
        variant: "success",
        type: "logout",
      });

      // We want to navigate after the notification so that the login input takes the focus away
      // from the alert
      this.$router.push("/login?logout=1");
    },
  },
};
</script>

<style lang="scss">
.layout-navbar {
  a.badge.badge-warning {
    &:hover,
    &:active,
    &:focus {
      color: #7e4c02;
      background-color: #fff3d2;
      box-shadow: none;
    }
  }

  &__dropdown__avatar {
    a.badge.badge-warning {
      position: absolute;
      top: -0.25rem;
      left: -0.25rem;
    }
  }
}
</style>
